import { useEffect, useState } from 'react'
import {
  Typography,
  Tabs,
  Tab,
  Box,
  Grid2 as Grid,
  useTheme
} from '@mui/material'
import { healthDataContent } from './data/health_data_content'
import { useParams } from 'react-router-dom'
import Reference from 'components/Reference'
import AccordionBase from 'components/Accordion'
import { getPatientHistoricalValues } from 'services/patient'
import {
  HistoricalValuesPeriodTypes,
  IPatientHistoricalValuesResponse
} from 'types/patient/types'
import GraphLinesWithBar from 'components/Graphs/LinesWithBar'
import NavLinkBack from 'components/NavLinkBack'
import PatientInfo from 'components/PatientInfo'
import { PATIENT_HEALTH_DATA } from 'navigation/CONSTANTS'
import HealthDataSummary from './components/summary'
import GraphBar from 'components/Graphs/Bar'
import { IDataEntry, PeriodTypes } from 'components/Graphs/types/types'
import { FloatingActionButton } from 'design-system/FloatingActionButton'
import AddIcon from '@mui/icons-material/Add'
import ManualInput from './components/manualinput'
import DayFilterPeriod from './components/dayFilterPeriod'
import dayjs, { Dayjs } from 'dayjs'

const periodsTypes: PeriodTypes[] = ['day', 'week', 'month', 'year']

const HealthDataDetailPage = () => {
  // query params
  const { patientId, data } = useParams<{
    patientId: string
    data: string
  }>()
  // theme
  const theme = useTheme()
  // state
  const [tabValue, setTabValue] = useState(0)
  const [historicalValues, setHistoricalValues] =
    useState<IPatientHistoricalValuesResponse[]>()
  const [state, setState] = useState<IStateStdTypes>('idle')

  const [modalVisible, setModalVisible] = useState<boolean>(false)

  const [dayFilter, setDayFilter] = useState<Dayjs | null>(dayjs())

  const fetchData = async () => {
    const dataKeys = [...(healthData.dataOptions?.auxData ?? []), data]
    const referenceDate = period === 'day' && !dayFilter?.isSame(dayjs(), "day") ? dayFilter?.toDate() : undefined

    const responses = await Promise.all(
      dataKeys.map(async (key, i) => ({
        response: await getPatientHistoricalValues(
          patientId ?? '',
          key ?? '',
          period,
          referenceDate
        ),
        index: i
      }))
    )

    if (responses.find(r => !r.response.success)) {
      setState('error')
    }

    const historicalValuesResponse: IPatientHistoricalValuesResponse[] = []
    responses
      .sort((a, b) => b.index - a.index)
      .forEach(r => {
        if (r.response.data) {
          historicalValuesResponse.push(r.response.data)
        }
      })
    setHistoricalValues([...historicalValuesResponse])
    setState('success')
  }

  useEffect(() => {
    setState('loading')
    fetchData()
  }, [tabValue, dayFilter])

  if (state === 'error' || !data) {
    // TODO: use default error component
    return <>Erro</>
  }

  const healthData = healthDataContent[data]
  const periods = periodsTypes
    .filter(p => !healthDataContent[data].graphOptions?.hidePeriod?.includes(p))
    .reduce(
      (acc, b, i) => {
        acc[i] = b
        return acc
      },
      {} as Record<number, HistoricalValuesPeriodTypes>
    )

  const period = periods[tabValue]

  const graphReferenceDate =
    period === 'day' && !dayFilter?.isSame(dayjs(), 'day') ? dayFilter : undefined

  const renderGraph = (dataEntry: IDataEntry[][]) => ({
    'lines-with-bar': (
      <GraphLinesWithBar
        loading={state === 'loading'}
        periods={period}
        data={dataEntry}
        dataType={healthData.dataOptions?.type ?? ''}
        referenceAreas={healthData.graphOptions?.referenceAreas}
        formatValue={healthData.dataOptions?.formatValue}
        yAxisDomain={healthData.graphOptions?.yAxisDomain}
        hasMinAndMax={healthData.graphOptions?.hasMaxAndMin ?? true}
        floatingAvg={healthData.graphOptions?.floatingAvg ?? false}
        referenceDate={graphReferenceDate}
      />
    ),
    'stacked-bar': (
      <GraphBar
        loading={state === 'loading'}
        data={dataEntry}
        periods={period}
        dataType={healthData.dataOptions?.type ?? ''}
        type='stacked'
        formatValue={healthData.dataOptions?.formatValue}
        formatY={healthData.graphOptions?.formatValue}
        reference={period === 'year' ? 'avg' : 'sum'}
        yAxisDomain={healthData.graphOptions?.yAxisDomain}
        useEndOfDay={healthData.graphOptions?.useEndOfDay}
        referenceDate={graphReferenceDate}
      />
    ),
    bar: (
      <GraphBar
        loading={state === 'loading'}
        data={dataEntry}
        periods={period}
        dataType={healthData.dataOptions?.type ?? ''}
        type='simple'
        formatValue={healthData.dataOptions?.formatValue}
        formatY={healthData.graphOptions?.formatValue}
        reference={'sum'}
        yAxisDomain={healthData.graphOptions?.yAxisDomain}
        referenceDate={graphReferenceDate}
      />
    )
  })

  const dataEntries: IDataEntry[][] =
    state === 'success' && historicalValues
      ? historicalValues.map(hv =>
          hv.values.map(v => ({
            ...v,
            name: healthDataContent[hv.data_type].title
          }))
        )
      : []

  const periodLabel = {
    day: 'Dia',
    week: 'Semana',
    month: 'Mês',
    year: 'Ano'
  }

  return (
    <Box
      sx={{
        padding: 2,
        maxWidth: 1200,
        margin: '0 auto',
        [theme.breakpoints.down('lg')]: {
          margin: 'inherit'
        }
      }}
    >
      <Grid container>
        <Grid size={12} paddingBlock={2}>
          <NavLinkBack
            to={`/${PATIENT_HEALTH_DATA(patientId)}`}
            title='Dados de Saúde'
          />
        </Grid>
      </Grid>
      <PatientInfo patientId={patientId ?? ''} />
      <Grid size={12} p={2} mt={2}>
        <Typography variant='h5' textAlign='center'>
          {healthData.title}
        </Typography>
      </Grid>
      <Tabs
        value={tabValue}
        onChange={(e, value) => {
          setTabValue(value)
          setDayFilter(dayjs())
        }}
        centered
        textColor='primary'
        indicatorColor='primary'
      >
        {Object.keys(periods).map((_, index) => (
          <Tab
            key={index}
            disabled={state === 'loading'}
            label={periodLabel[periods[index]]}
          />
        ))}
      </Tabs>
      <Box
        sx={{
          width: '100%',
          flex: 1,
          marginTop: 2,
          paddingTop: 2,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px',
          background: theme.palette.background.paper,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',

          height: 550,

          [theme.breakpoints.down('sm')]: {
            height: 400,
            paddingRight: '10%!important'
          },
          [theme.breakpoints.down('md')]: {
            paddingRight: '5%'
          },
          [theme.breakpoints.down('lg')]: {
            height: 500,
            paddingRight: '2.5%'
          }
        }}
      >
        <Grid size={12} textAlign='right'>
          <DayFilterPeriod
            show={period === 'day'}
            value={dayFilter}
            onFilterChange={setDayFilter}
          />
        </Grid>
        <Box
          sx={{
            width: '100%',
            flex: 1,
            height: 450,
            marginTop: 2,
            paddingTop: 2,
            paddingRight: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',

            [theme.breakpoints.down('sm')]: {
              height: 300,
              paddingRight: '10%!important'
            },
            [theme.breakpoints.down('md')]: {
              height: 400,
              paddingRight: '5%'
            },
            [theme.breakpoints.down('lg')]: {
              height: 400,
              paddingRight: '2.5%'
            }
          }}
        >
          {
            renderGraph(dataEntries)[
              healthData.graphOptions?.type ?? 'lines-with-bar'
            ]
          }
        </Box>
      </Box>
      <HealthDataSummary
        loading={state === 'loading'}
        historicalValues={historicalValues ?? []}
        separator={healthData.dataOptions?.separator}
        valueType={healthData.dataOptions?.type ?? ''}
        showAllData={healthData.dataOptions?.showAllInSummary}
        formatValue={healthData.dataOptions?.formatValue}
        summaryData={healthData.dataOptions?.summaryTypes}
        period={period}
        mean={healthData.dataOptions?.mean}
      />
      {healthData.info?.map((info, index) => (
        <AccordionBase
          key={index}
          sx={{ mt: 2 }}
          defaultExpanded={index === 0}
          title={info.title}
        >
          <Typography>
            <span dangerouslySetInnerHTML={{ __html: info.description }} />
          </Typography>
        </AccordionBase>
      ))}
      {healthData.references && (
        <AccordionBase
          sx={{ mt: 2 }}
          defaultExpanded={false}
          title='Referências'
        >
          <Grid container spacing={2}>
            {healthData.references.map((ref: any, index: number) => (
              <Grid
                key={index}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}
              >
                <Reference link={ref.link} title={ref.title} />
              </Grid>
            ))}
          </Grid>
        </AccordionBase>
      )}
      {healthData.manualInputGuide && (
        <>
          <FloatingActionButton
            sx={{
              position: 'sticky',
              bottom: '5%',
              float: 'right',
              margin: '16px'
            }}
            onClick={() => {
              setModalVisible(true)
            }}
          >
            <AddIcon />
          </FloatingActionButton>
          <ManualInput
            visible={modalVisible}
            onClose={() => {
              setModalVisible(false)
            }}
            onSave={fetchData}
          />
        </>
      )}
    </Box>
  )
}

export default HealthDataDetailPage
